/** Stable identities remain in files; descriptions always come from the catalog. */
export type OptionKey = string | number;
export interface NamedOption<Key extends OptionKey = string> {
  value: Key;
  name: string;
}
export interface ConfigurationOption {
  configKey: number;
  displayName: string;
}

export interface DisplayOption {
  value: string;
  label: string;
}
export type EquipmentOption =
  | NamedOption<OptionKey>
  | { value: OptionKey; displayName: string }
  | DisplayOption
  | ConfigurationOption
  | { simulationModel: string; displayName: string }
  | { model: string; description?: string }
  | { id: OptionKey; displayName: string }
  | { id: OptionKey; name: string };

export const getOptionKey = (option: EquipmentOption): string =>
  String(
    'model' in option
      ? option.model
      : 'simulationModel' in option
        ? option.simulationModel
        : 'configKey' in option
          ? option.configKey
          : 'id' in option
            ? option.id
            : option.value,
  );

export const getOptionLabel = (option: EquipmentOption): string =>
  'model' in option
    ? option.model
    : 'label' in option
      ? option.label
      : 'name' in option
        ? option.name
        : option.displayName;

export const toDisplayOption = (option: EquipmentOption): DisplayOption => ({
  value: getOptionKey(option),
  label: getOptionLabel(option),
});

export const findOption = <T extends EquipmentOption>(
  options: readonly T[] | undefined | null,
  key: OptionKey | undefined | null,
): T | undefined =>
  key === undefined || key === null
    ? undefined
    : options?.find((option) => getOptionKey(option) === String(key));

export const unavailableOptionLabel = (key: OptionKey, savedLabel?: string) =>
  `${savedLabel ?? String(key)} (unavailable)`;

export const resolveOption = <T extends EquipmentOption>(
  options: readonly T[] | undefined | null,
  key: OptionKey | undefined | null,
  savedLabel?: string,
) => {
  if (key === undefined || key === null || key === '') {
    return { status: 'empty' as const, option: undefined, label: '' };
  }
  const option = findOption(options, key);
  return option
    ? { status: 'resolved' as const, option, label: getOptionLabel(option) }
    : {
        status: 'unavailable' as const,
        option: undefined,
        label: unavailableOptionLabel(key, savedLabel),
      };
};
